// QuestionParagraphText.js
import React, { useState, useRef, useEffect } from "react";
import { Question, QuestionFactory, Serializer } from "survey-core";
import { ReactQuestionFactory } from "survey-react-ui";

const QUESTION_TYPE = "paragraphtext";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL
========================================================= */
class QuestionParagraphTextModel extends Question {
  constructor(name) {
    super(name);

    // Set default values for paragraph
    this.setPropertyValue(
      "content",
      "<p>Enter your paragraph text here. You can format it using the toolbar.</p>",
    );
    this.setPropertyValue("alignment", "left");
    this.setPropertyValue("fontSize", "16px");
    this.setPropertyValue("lineHeight", "1.6");
    this.setPropertyValue("fontWeight", "normal");
    this.setPropertyValue("fontStyle", "normal");
    this.setPropertyValue("color", "#333333");
    this.setPropertyValue("backgroundColor", "transparent");
    this.setPropertyValue("padding", "15px");
    this.setPropertyValue("margin", "0 0 20px 0");
    this.setPropertyValue("border", "none");
    this.setPropertyValue("borderRadius", "0px");
    this.setPropertyValue("textIndent", "0px");
    this.setPropertyValue("textAlignLast", "auto");
    this.setPropertyValue("wordSpacing", "normal");
    this.setPropertyValue("letterSpacing", "normal");
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Content (HTML supported)
  get content() {
    return this.getPropertyValue(
      "content",
      "<p>Enter your paragraph text here.</p>",
    );
  }

  set content(val) {
    this.setPropertyValue("content", val);
  }

  // Alignment
  get alignment() {
    return this.getPropertyValue("alignment", "left");
  }

  set alignment(val) {
    this.setPropertyValue("alignment", val);
  }

  // Font size
  get fontSize() {
    return this.getPropertyValue("fontSize", "16px");
  }

  set fontSize(val) {
    this.setPropertyValue("fontSize", val);
  }

  // Line height
  get lineHeight() {
    return this.getPropertyValue("lineHeight", "1.6");
  }

  set lineHeight(val) {
    this.setPropertyValue("lineHeight", val);
  }

  // Font weight
  get fontWeight() {
    return this.getPropertyValue("fontWeight", "normal");
  }

  set fontWeight(val) {
    this.setPropertyValue("fontWeight", val);
  }

  // Font style
  get fontStyle() {
    return this.getPropertyValue("fontStyle", "normal");
  }

  set fontStyle(val) {
    this.setPropertyValue("fontStyle", val);
  }

  // Text color
  get color() {
    return this.getPropertyValue("color", "#333333");
  }

  set color(val) {
    this.setPropertyValue("color", val);
  }

  // Background color
  get backgroundColor() {
    return this.getPropertyValue("backgroundColor", "transparent");
  }

  set backgroundColor(val) {
    this.setPropertyValue("backgroundColor", val);
  }

  // Padding
  get padding() {
    return this.getPropertyValue("padding", "15px");
  }

  set padding(val) {
    this.setPropertyValue("padding", val);
  }

  // Border
  get border() {
    return this.getPropertyValue("border", "none");
  }

  set border(val) {
    this.setPropertyValue("border", val);
  }

  // Border radius
  get borderRadius() {
    return this.getPropertyValue("borderRadius", "0px");
  }

  set borderRadius(val) {
    this.setPropertyValue("borderRadius", val);
  }

  // Text indent (for first line)
  get textIndent() {
    return this.getPropertyValue("textIndent", "0px");
  }

  set textIndent(val) {
    this.setPropertyValue("textIndent", val);
  }

  // Get CSS style object for paragraph
  get paragraphStyle() {
    return {
      textAlign: this.alignment,
      fontSize: this.fontSize,
      lineHeight: this.lineHeight,
      fontWeight: this.fontWeight,
      fontStyle: this.fontStyle,
      color: this.color,
      backgroundColor: this.backgroundColor,
      padding: this.padding,
      margin: this.margin,
      border: this.border,
      borderRadius: this.borderRadius,
      textIndent: this.textIndent,
      textAlignLast: this.textAlignLast,
      wordSpacing: this.wordSpacing,
      letterSpacing: this.letterSpacing,
      outline: "none",
      width: "100%",
      minHeight: "100px",
      fontFamily: "inherit",
      transition: "all 0.2s ease",
    };
  }

  // Get inline style string for HTML rendering
  get inlineStyle() {
    return Object.entries(this.paragraphStyle)
      .map(([key, value]) => `${key}: ${value};`)
      .join(" ");
  }
}

/* =========================================================
   REACT RENDERER with Advanced Rich Text Editor
========================================================= */
const ParagraphTextComponent = (props) => {
  const question = props.question;
  const [isEditing, setIsEditing] = useState(false);
  const [localContent, setLocalContent] = useState(question.content);
  const editorRef = useRef(null);
  const [activeFormat, setActiveFormat] = useState({
    bold: false,
    italic: false,
    underline: false,
  });

  useEffect(() => {
    setLocalContent(question.content);

    // Check current formatting when content changes
    if (editorRef.current) {
      const editor = editorRef.current;
      setActiveFormat({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    }
  }, [question.content]);

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.innerHTML =
        question.content ||
        "<p>Enter your paragraph text here. You can format it using the toolbar.</p>";
      editorRef.current.focus();
    }
  }, [isEditing]);

  if (!question) {
    return <div>Loading paragraph...</div>;
  }

  // Handle content change
  const handleContentChange = (newContent) => {
    setLocalContent(newContent);
    question.content = newContent;
  };

  // Toggle editing mode
  const toggleEdit = () => {
    if (question.isReadOnly) return;
    setIsEditing((prev) => !prev);
  };

  // Formatting functions
  const applyFormat = (command, value = null, formatName = null) => {
    if (!editorRef.current) return;

    document.execCommand(command, false, value);
    editorRef.current.focus();

    // Update active format state
    if (formatName) {
      setActiveFormat((prev) => ({
        ...prev,
        [formatName]: document.queryCommandState(command),
      }));
    }

    // Update question with formatted HTML
    const newHtml = editorRef.current.innerHTML;
    handleContentChange(newHtml);
  };

  // Insert list
  const insertList = (ordered = false) => {
    if (!editorRef.current) return;
    applyFormat(ordered ? "insertOrderedList" : "insertUnorderedList");
  };

  // Insert link
  const insertLink = () => {
    const url = prompt("Enter URL:", "https://");
    if (url) {
      applyFormat("createLink", url);
    }
  };

  // Clear formatting
  const clearFormatting = () => {
    if (!editorRef.current) return;
    applyFormat("removeFormat");
    setActiveFormat({ bold: false, italic: false, underline: false });
  };

  // Render editing mode with advanced toolbar
  const renderEditor = () => {
    return (
      <div
        style={{
          width: "100%",
          border: "1px solid #3661EB",
          borderRadius: "6px",
          backgroundColor: "white",
          boxShadow: "0 2px 8px rgba(54, 97, 235, 0.1)",
        }}
      >
        {/* Advanced formatting toolbar */}
        <div
          style={{
            padding: "10px 12px",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #dee2e6",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            alignItems: "center",
            borderRadius: "6px 6px 0 0",
          }}
        >
          {/* Font style buttons */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              borderRight: "1px solid #ddd",
              paddingRight: "8px",
            }}
          >
            {/* Bold */}
            <button
              type="button"
              onClick={() => applyFormat("bold", null, "bold")}
              style={{
                padding: "6px 10px",
                backgroundColor: activeFormat.bold ? "#3661EB" : "#fff",
                color: activeFormat.bold ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
                fontWeight: "bold",
                minWidth: "36px",
              }}
              title="Bold (Ctrl+B)"
            >
              <strong>B</strong>
            </button>

            {/* Italic */}
            <button
              type="button"
              onClick={() => applyFormat("italic", null, "italic")}
              style={{
                padding: "6px 10px",
                backgroundColor: activeFormat.italic ? "#3661EB" : "#fff",
                color: activeFormat.italic ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
                fontStyle: "italic",
                minWidth: "36px",
              }}
              title="Italic (Ctrl+I)"
            >
              <em>I</em>
            </button>

            {/* Underline */}
            <button
              type="button"
              onClick={() => applyFormat("underline", null, "underline")}
              style={{
                padding: "6px 10px",
                backgroundColor: activeFormat.underline ? "#3661EB" : "#fff",
                color: activeFormat.underline ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
                textDecoration: "underline",
                minWidth: "36px",
              }}
              title="Underline (Ctrl+U)"
            >
              U
            </button>
          </div>

          {/* Text alignment */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              borderRight: "1px solid #ddd",
              paddingRight: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => applyFormat("justifyLeft")}
              style={{
                padding: "6px 10px",
                backgroundColor:
                  question.alignment === "left" ? "#79A1DE" : "#fff",
                color: question.alignment === "left" ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Align Left"
            >
              ⬅
            </button>
            <button
              type="button"
              onClick={() => applyFormat("justifyCenter")}
              style={{
                padding: "6px 10px",
                backgroundColor:
                  question.alignment === "center" ? "#79A1DE" : "#fff",
                color: question.alignment === "center" ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Align Center"
            >
              ↔
            </button>
            <button
              type="button"
              onClick={() => applyFormat("justifyRight")}
              style={{
                padding: "6px 10px",
                backgroundColor:
                  question.alignment === "right" ? "#79A1DE" : "#fff",
                color: question.alignment === "right" ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Align Right"
            >
              ➡
            </button>
            <button
              type="button"
              onClick={() => applyFormat("justifyFull")}
              style={{
                padding: "6px 10px",
                backgroundColor:
                  question.alignment === "justify" ? "#79A1DE" : "#fff",
                color: question.alignment === "justify" ? "white" : "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Justify"
            >
              ⇔
            </button>
          </div>

          {/* Lists */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              borderRight: "1px solid #ddd",
              paddingRight: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => insertList(false)}
              style={{
                padding: "6px 10px",
                backgroundColor: "#fff",
                color: "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Bulleted List"
            >
              • List
            </button>
            <button
              type="button"
              onClick={() => insertList(true)}
              style={{
                padding: "6px 10px",
                backgroundColor: "#fff",
                color: "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Numbered List"
            >
              1. List
            </button>
          </div>

          {/* Text color and background */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              borderRight: "1px solid #ddd",
              paddingRight: "8px",
            }}
          >
            <input
              type="color"
              onChange={(e) => applyFormat("foreColor", e.target.value)}
              defaultValue={question.color}
              style={{
                width: "36px",
                height: "36px",
                padding: "2px",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Text Color"
            />
            <input
              type="color"
              onChange={(e) => applyFormat("backColor", e.target.value)}
              defaultValue={question.backgroundColor}
              style={{
                width: "36px",
                height: "36px",
                padding: "2px",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Background Color"
            />
          </div>

          {/* Font size */}
          <select
            onChange={(e) => applyFormat("fontSize", e.target.value)}
            defaultValue="3"
            style={{
              padding: "6px",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              backgroundColor: "#fff",
              minWidth: "80px",
            }}
            title="Font Size"
          >
            <option value="1">Small (12px)</option>
            <option value="2">Normal (14px)</option>
            <option value="3">Medium (16px)</option>
            <option value="4">Large (18px)</option>
            <option value="5">XL (20px)</option>
            <option value="6">XXL (24px)</option>
            <option value="7">Huge (28px)</option>
          </select>

          {/* Additional formatting */}
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              type="button"
              onClick={insertLink}
              style={{
                padding: "6px 10px",
                backgroundColor: "#fff",
                color: "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Insert Link"
            >
              🔗
            </button>
            <button
              type="button"
              onClick={clearFormatting}
              style={{
                padding: "6px 10px",
                backgroundColor: "#fff",
                color: "#495057",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              title="Clear Formatting"
            >
              ✕
            </button>
          </div>

          {/* Done button */}
          <button
            type="button"
            onClick={toggleEdit}
            style={{
              marginLeft: "auto",
              padding: "6px 16px",
              backgroundColor: "#3661EB",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
              whiteSpace: "nowrap",
            }}
          >
            Save & Close
          </button>
        </div>

        {/* Editable content area */}
        <div
          ref={editorRef}
          contentEditable={!question.isReadOnly}
          onInput={(e) => {
            const html = e.currentTarget.innerHTML;
            question.content = html; // sync only to SurveyJS
          }}
          onKeyDown={(e) => {
            if (e.ctrlKey && e.key === "b") {
              e.preventDefault();
              applyFormat("bold", null, "bold");
            }
            if (e.ctrlKey && e.key === "i") {
              e.preventDefault();
              applyFormat("italic", null, "italic");
            }
            if (e.ctrlKey && e.key === "u") {
              e.preventDefault();
              applyFormat("underline", null, "underline");
            }
          }}
          style={{
            ...question.paragraphStyle,
            minHeight: "150px",
            padding: "15px",
            outline: "none",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            overflowY: "auto",
            maxHeight: "400px",
          }}
          className="paragraph-editor"
          data-placeholder="Start typing your paragraph here..."
        />

        {/* Character count */}
        <div
          style={{
            padding: "8px 15px",
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #dee2e6",
            fontSize: "12px",
            color: "#6c757d",
            display: "flex",
            justifyContent: "space-between",
            borderRadius: "0 0 6px 6px",
          }}
        >
          <span>HTML editing enabled</span>
          <span>{localContent.replace(/<[^>]*>/g, "").length} characters</span>
        </div>
      </div>
    );
  };

  // Render view mode (read-only)
  const renderView = () => {
    return (
      <div
        onClick={toggleEdit}
        style={{
          ...question.paragraphStyle,
          cursor: question.isReadOnly ? "text" : "pointer",
          border: question.isReadOnly ? "none" : "1px dashed transparent",
          transition: "all 0.2s",
          position: "relative",
        }}
        className={!question.isReadOnly ? "paragraph-text-editable" : ""}
      >
        {/* Show HTML content */}
        <div
          dangerouslySetInnerHTML={{ __html: localContent }}
          style={{ minHeight: "50px", textWrap: "wrap" }}
        />

        {/* Edit hint (only when not read-only) */}
        {!question.isReadOnly && !isEditing && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              fontSize: "12px",
              color: "#6c757d",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px 8px",
              borderRadius: "4px",
              opacity: 0,
              transition: "opacity 0.3s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              pointerEvents: "none",
            }}
            className="edit-hint"
          >
            ✏️ Click to edit paragraph
          </div>
        )}

        {/* Add CSS for hover effect */}
        <style>
          {`
            .paragraph-text-editable:hover {
              border-color: #3661EB !important;
              background-color: rgba(54, 97, 235, 0.02);
            }
            .paragraph-text-editable:hover .edit-hint {
              opacity: 1 !important;
            }
          `}
        </style>
      </div>
    );
  };

  return isEditing ? renderEditor() : renderView();
};

/* =========================================================
   REGISTRATION
========================================================= */
export function registerParagraphTextQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "content",
        type: "html",
        category: "content",
        displayName: "Paragraph Content",
        default:
          "<p>Enter your paragraph text here. You can format it using the toolbar.</p>",
        visible: true,
      },
      {
        name: "alignment",
        type: "text",
        category: "style",
        displayName: "Text Alignment",
        default: "left",
        choices: ["left", "center", "right", "justify"],
        visible: true,
      },
      {
        name: "fontSize",
        type: "text",
        category: "style",
        displayName: "Font Size",
        default: "16px",
        choices: ["12px", "14px", "16px", "18px", "20px", "22px", "24px"],
        visible: true,
      },
      {
        name: "lineHeight",
        type: "text",
        category: "style",
        displayName: "Line Height",
        default: "1.6",
        choices: ["1", "1.2", "1.4", "1.6", "1.8", "2"],
        visible: true,
      },
      {
        name: "fontWeight",
        type: "text",
        category: "style",
        displayName: "Font Weight",
        default: "normal",
        choices: ["normal", "300", "400", "500", "600", "bold", "700", "800"],
        visible: true,
      },
      {
        name: "fontStyle",
        type: "text",
        category: "style",
        displayName: "Font Style",
        default: "normal",
        choices: ["normal", "italic", "oblique"],
        visible: true,
      },
      {
        name: "color",
        type: "color",
        category: "style",
        displayName: "Text Color",
        default: "#333333",
        visible: true,
      },
      {
        name: "backgroundColor",
        type: "color",
        category: "style",
        displayName: "Background Color",
        default: "transparent",
        visible: true,
      },
      {
        name: "padding",
        type: "text",
        category: "spacing",
        displayName: "Padding",
        default: "15px",
        visible: true,
      },
      {
        name: "border",
        type: "text",
        category: "border",
        displayName: "Border",
        default: "none",
        choices: [
          "none",
          "1px solid #ddd",
          "1px solid #3661EB",
          "2px solid #79A1DE",
        ],
        visible: true,
      },
      {
        name: "borderRadius",
        type: "text",
        category: "border",
        displayName: "Border Radius",
        default: "0px",
        choices: ["0px", "4px", "8px", "12px"],
        visible: true,
      },
      {
        name: "textIndent",
        type: "text",
        category: "style",
        displayName: "First Line Indent",
        default: "0px",
        choices: ["0px", "20px", "40px", "60px"],
        visible: true,
      },
    ],
    () => new QuestionParagraphTextModel(""),
    "question",
  );

  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionParagraphTextModel(name),
  );

  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) => (
    <ParagraphTextComponent {...props} />
  ));
}

// Auto-register
if (typeof window !== "undefined") {
  registerParagraphTextQuestion();
}

export default QuestionParagraphTextModel;
